import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePrevious } from 'react-use';
import styles from './StickyBar.module.scss';

const StickyBar = ({
  children,
  className,
  offset,
  upOffset,
  onlyStickyOnUp,
  zIndex,
  nonStickyHidden
}) => {
  const wrapperRef = useRef(null);
  const phantomRef = useRef(null);

  const [wrapperYOffset, setWrapperYOffset] = useState(null);
  const [documentYScroll, setDocumentYScroll] = useState(
    window.pageYOffset || document.documentElement.scrollTop
  );
  const lastDocumentYScroll = usePrevious(documentYScroll);

  const scrollDirection = useMemo(
    () => (documentYScroll >= (lastDocumentYScroll || documentYScroll)
      ? 'DOWN'
      : 'UP'),
    [documentYScroll, lastDocumentYScroll],
  );
  const hasScrolledPast = useMemo(
    () => wrapperYOffset && documentYScroll + offset >= wrapperYOffset,
    [documentYScroll, wrapperYOffset, offset],
  );
  const isSticky = useMemo(
    () => hasScrolledPast
      && (onlyStickyOnUp ? scrollDirection === 'UP' : !onlyStickyOnUp),
    [scrollDirection, hasScrolledPast, onlyStickyOnUp],
  );

  useEffect(() => {
    const handleOnScroll = () => {
      setDocumentYScroll(document.documentElement.scrollTop);
    };

    window.addEventListener('scroll', handleOnScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleOnScroll);
    };
  }, []);

  useEffect(() => {
    if (!isSticky) {
      if (wrapperRef.current) {
        setWrapperYOffset(wrapperRef.current.offsetTop);
      }
    }
  }, [isSticky]);

  return (
    <>
      {/* This is needed to prevent page jump when sticky bar becomes position fixed. */}
      <div
        ref={phantomRef}
        style={{
          height: wrapperRef.current ? wrapperRef.current.offsetHeight : 0,
          display: isSticky ? 'block' : 'none',
        }}
      />
      <div
        ref={wrapperRef}
        style={{
          top: scrollDirection === 'UP' ? upOffset || offset : offset,
          zIndex,
        }}
        className={classnames(className, {
          [styles.sticky]: isSticky,
          [styles.hidden]: !isSticky && nonStickyHidden,
        })}
      >
        {children}
      </div>
    </>
  );
};

StickyBar.propTypes = {
  children: PropTypes.element,
  className: PropTypes.string,
  offset: PropTypes.number,
  upOffset: PropTypes.number,
  onlyStickyOnUp: PropTypes.bool,
  zIndex: PropTypes.number,
  nonStickyHidden: PropTypes.bool
};

StickyBar.defaultProps = {
  className: '',
  offset: 0,
  upOffset: 0,
  onlyStickyOnUp: false,
  zIndex: 20,
  children: null,
  nonStickyHidden: false
};

export { StickyBar };
